import React, { useState, useCallback } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import media from "../style/media";
import TopBar from "../components/TopBar";
import SettingsBars from "../components/SettingsBars";
import { Flex } from "../components/shared/Flex";
import { useLazyGetHealthReportPDFQuery } from "../services/healthAnalysisApi";
import { useCameras } from "../hooks";

import { PageContainer, ContentWrapper, Header, Title, Subtitle, GlassCard } from '../style/layout';
import { CheckCircle, FileText, PlusCircle, ArrowRight } from 'lucide-react';

const SuccessIconWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 2rem;
  color: #10b981;

  svg {
    width: 80px;
    height: 80px;
    filter: drop-shadow(0 4px 12px rgba(16, 185, 129, 0.2));
    animation: scaleIn 0.5s cubic-bezier(0.16, 1, 0.3, 1);
  }

  @keyframes scaleIn {
    from { transform: scale(0); opacity: 0; }
    to { transform: scale(1); opacity: 1; }
  }
`;

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 1rem;
`;

const PrimaryButton = styled.button`
  width: 100%;
  padding: 1.25rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 20px;
  font-size: 1.125rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(102, 126, 234, 0.35);
  }

  &:active {
    transform: translateY(0);
  }
`;

const SecondaryButton = styled.button`
  width: 100%;
  padding: 1.25rem;
  background: #f8fafc;
  color: #475569;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  font-size: 1.125rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;

  &:hover {
    background: #f1f5f9;
    color: #0f172a;
    border-color: #cbd5e1;
  }
`;

const SuccessAssessment = () => {
  const navigate = useNavigate();
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isLicenseValid, setIsLicenseValid] = useState(false);
  const cameras = useCameras();

  const [getHealthReportPDF] = useLazyGetHealthReportPDFQuery();

  const toggleSettings = useCallback(() => {
    setIsSettingsOpen(prev => !prev);
  }, []);

  const handleCloseSettings = useCallback(() => {
    setIsSettingsOpen(false);
  }, []);

  const updateLicenseStatus = useCallback((valid: boolean) => {
    setIsLicenseValid(valid);
  }, []);

  const handleGetReport = async () => {
    const id = localStorage.getItem("health_assessment_id");

    if (!id) {
      alert("Assessment ID not found!");
      return;
    }

    try {
      const pdfBlob = await getHealthReportPDF(id).unwrap();
      const fileURL = URL.createObjectURL(
        new Blob([pdfBlob], { type: "application/pdf" })
      );

      window.open(fileURL, "_blank");

      const link = document.createElement("a");
      link.href = fileURL;
      link.download = `health_report_${id}.pdf`;
      link.click();
    } catch (error) {
      console.error("Error downloading PDF:", error);
      alert("Unable to load report PDF");
    }
  };

  return (
    <PageContainer>
      <TopBar onSettingsClick={toggleSettings} isMeasuring={false} />

      <SettingsBars
        open={isSettingsOpen}
        onClose={handleCloseSettings}
        cameras={cameras}
        isLicenseValid={isLicenseValid}
      />

      <ContentWrapper>
        <GlassCard style={{ textAlign: 'center' }}>
          <SuccessIconWrapper>
            <CheckCircle />
          </SuccessIconWrapper>

          <Header style={{ textAlign: 'center' }}>
            <Title>Assessment Complete!</Title>
            <Subtitle>
              Your health assessment has been analyzed successfully. You can now download your report or start a new session.
            </Subtitle>
          </Header>

          <Actions>
            <PrimaryButton onClick={handleGetReport}>
              <FileText size={22} />
              <span>Download Health Report (PDF)</span>
            </PrimaryButton>

            <SecondaryButton onClick={() => navigate("/patient")}>
              <PlusCircle size={22} />
              <span>Start New Assessment</span>
            </SecondaryButton>

            <button
              onClick={() => navigate("/dashboard")}
              style={{
                background: 'none',
                border: 'none',
                color: '#64748b',
                fontWeight: 600,
                fontSize: '0.95rem',
                cursor: 'pointer',
                marginTop: '1rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem'
              }}
            >
              <span>Return to Dashboard</span>
              <ArrowRight size={16} />
            </button>
          </Actions>
        </GlassCard>
      </ContentWrapper>
    </PageContainer>
  );
};

export default SuccessAssessment;

